import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../admin/dashboard/components/AdminLayout';
import { COLORS } from '../../../core/constants/colors';
import { tracxnVerificationService } from '../services/tracxn-verification.service';
import {
  TracxnImport,
  PendingCompany,
  AuditLog,
} from '../types';

type TabType = 'upload' | 'imports' | 'pending' | 'audit';

export const TracxnVerificationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('upload');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sendNotifications, setSendNotifications] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<TracxnImport[]>([]);

  // Import history state
  const [imports, setImports] = useState<TracxnImport[]>([]);
  const [isLoadingImports, setIsLoadingImports] = useState(false);
  const [selectedImport, setSelectedImport] = useState<TracxnImport | null>(null);

  // Pending companies state
  const [pendingCompanies, setPendingCompanies] = useState<PendingCompany[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [verifyingCompanyId, setVerifyingCompanyId] = useState<number | null>(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditCompanyFilter, setAuditCompanyFilter] = useState<string>('');
  const [auditActionFilter, setAuditActionFilter] = useState<string>('');

  useEffect(() => {
    if (activeTab === 'imports') {
      loadImports();
    } else if (activeTab === 'pending') {
      loadPendingCompanies();
    } else if (activeTab === 'audit') {
      loadAuditLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    setIsUploading(true);
    setMessage(null);
    setError(null);

    try {
      const results = await tracxnVerificationService.uploadFiles(
        selectedFiles,
        sendNotifications
      );
      setUploadResults(results);
      setMessage(`Successfully processed ${results.length} file(s).`);
      setSelectedFiles([]);
      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      setError(err.message || 'Failed to upload files.');
    } finally {
      setIsUploading(false);
    }
  };

  const loadImports = async () => {
    setIsLoadingImports(true);
    try {
      const data = await tracxnVerificationService.getImports(50, 0);
      setImports(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load import history.');
    } finally {
      setIsLoadingImports(false);
    }
  };

  const loadPendingCompanies = async () => {
    setIsLoadingPending(true);
    try {
      const data = await tracxnVerificationService.getPendingCompanies(100, 0);
      setPendingCompanies(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending companies.');
    } finally {
      setIsLoadingPending(false);
    }
  };

  const loadAuditLogs = async () => {
    setIsLoadingAudit(true);
    try {
      const companyId = auditCompanyFilter ? parseInt(auditCompanyFilter) : undefined;
      const action = auditActionFilter || undefined;
      const data = await tracxnVerificationService.getAuditLogs(companyId, action, 100, 0);
      setAuditLogs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit logs.');
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const handleVerifyCompany = async (companyId: number, verified: boolean) => {
    setVerifyingCompanyId(companyId);
    setError(null);
    setMessage(null);

    try {
      await tracxnVerificationService.verifyCompany(companyId, {
        pending_verification: !verified,
      });
      setMessage(`Company ${verified ? 'verified' : 'unverified'} successfully.`);
      await loadPendingCompanies();
    } catch (err: any) {
      setError(err.message || 'Failed to update verification status.');
    } finally {
      setVerifyingCompanyId(null);
    }
  };

  const handleSendDailySummary = async () => {
    setMessage(null);
    setError(null);
    try {
      await tracxnVerificationService.sendDailySummary();
      setMessage('Daily summary email sent successfully.');
    } catch (err: any) {
      setError(err.message || 'Failed to send daily summary.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return { bg: '#D1FAE5', text: '#059669' };
      case 'processing':
      case 'pending':
        return { bg: '#FEF3C7', text: '#D97706' };
      case 'failed':
      case 'error':
        return { bg: '#FFEBEE', text: '#C62828' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  return (
    <AdminLayout
      pageTitle="Tracxn Verification"
      pageDescription="Upload Tracxn data and manage company verification workflow"
    >
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-6">
          {/* Messages */}
          {message && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">{message}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="border-b flex items-center gap-1 px-2">
              {[
                { key: 'upload', label: 'Upload Files' },
                { key: 'imports', label: 'Import History' },
                { key: 'pending', label: 'Pending Verification' },
                { key: 'audit', label: 'Audit Logs' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as TabType)}
                  className={`px-4 py-3 font-medium text-sm transition-all ${
                    activeTab === tab.key
                      ? 'border-b-2 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={{
                    borderColor: activeTab === tab.key ? COLORS.admin.primary : 'transparent',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Upload Tab */}
              {activeTab === 'upload' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#1F2937' }}>
                      Upload Tracxn Files
                    </h3>
                    <p className="text-sm text-gray-600">
                      Upload CSV or Excel files exported from Tracxn. Supported columns: Name, Website, Description, Industry, Location, Founded, Employees
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Select Files (CSV, XLSX, XLS)
                      </label>
                      <input
                        id="file-input"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        multiple
                        onChange={handleFileSelect}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {selectedFiles.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          {selectedFiles.length} file(s) selected: {selectedFiles.map(f => f.name).join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="notifications"
                        checked={sendNotifications}
                        onChange={(e) => setSendNotifications(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <label htmlFor="notifications" className="text-sm text-gray-700 cursor-pointer">
                        Send email notifications to admins
                      </label>
                    </div>

                    <button
                      onClick={handleUpload}
                      disabled={isUploading || selectedFiles.length === 0}
                      className="px-6 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: COLORS.admin.primary }}
                    >
                      {isUploading ? 'Uploading...' : 'Upload & Process'}
                    </button>
                  </div>

                  {/* Upload Results */}
                  {uploadResults.length > 0 && (
                    <div className="mt-6 space-y-4">
                      <h4 className="font-bold text-gray-700">Upload Results</h4>
                      {uploadResults.map((result) => {
                        const statusColors = getStatusColor(result.status);
                        return (
                          <div key={result.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900">{result.file_name}</p>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                              >
                                {result.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Created: </span>
                                <span className="text-green-600 font-semibold">{result.companies_created}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Updated: </span>
                                <span className="text-blue-600 font-semibold">{result.companies_updated}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Failed: </span>
                                <span className="text-red-600 font-semibold">{result.companies_failed}</span>
                              </div>
                            </div>
                            {result.error_log && result.error_log.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-red-600">Errors:</p>
                                <ul className="list-disc list-inside text-xs text-red-600 mt-1">
                                  {result.error_log.slice(0, 5).map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Import History Tab */}
              {activeTab === 'imports' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                      Import History
                    </h3>
                    <button
                      onClick={loadImports}
                      disabled={isLoadingImports}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 disabled:opacity-50"
                    >
                      Refresh
                    </button>
                  </div>

                  {isLoadingImports ? (
                    <div className="text-center py-12">
                      <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                        style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                      />
                    </div>
                  ) : imports.length > 0 ? (
                    <div className="space-y-3">
                      {imports.map((imp) => {
                        const statusColors = getStatusColor(imp.status);
                        return (
                          <div
                            key={imp.id}
                            className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setSelectedImport(selectedImport?.id === imp.id ? null : imp)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{imp.file_name}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(imp.created_at).toLocaleString()}
                                </p>
                              </div>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                              >
                                {imp.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                              <div>
                                <span className="text-gray-600">Created: </span>
                                <span className="text-green-600 font-semibold">{imp.companies_created}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Updated: </span>
                                <span className="text-blue-600 font-semibold">{imp.companies_updated}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Failed: </span>
                                <span className="text-red-600 font-semibold">{imp.companies_failed}</span>
                              </div>
                            </div>
                            {selectedImport?.id === imp.id && imp.error_log && imp.error_log.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-medium text-red-600 mb-1">Error Log:</p>
                                <ul className="list-disc list-inside text-xs text-red-600 space-y-1">
                                  {imp.error_log.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No import history found
                    </div>
                  )}
                </div>
              )}

              {/* Pending Verification Tab */}
              {activeTab === 'pending' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                      Pending Verification ({pendingCompanies.length})
                    </h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSendDailySummary}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:shadow-md"
                        style={{ backgroundColor: COLORS.admin.primary }}
                      >
                        Send Daily Summary
                      </button>
                      <button
                        onClick={loadPendingCompanies}
                        disabled={isLoadingPending}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 disabled:opacity-50"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>

                  {isLoadingPending ? (
                    <div className="text-center py-12">
                      <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                        style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                      />
                    </div>
                  ) : pendingCompanies.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Company</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Website</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Industry</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Location</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Created</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {pendingCompanies.map((company) => (
                            <tr key={company.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-900">{company.name}</p>
                                {company.description && (
                                  <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                                    {company.description}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {company.website ? (
                                  <a
                                    href={company.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {company.website.slice(0, 30)}...
                                  </a>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700">{company.industry || '-'}</td>
                              <td className="px-4 py-3 text-gray-700">{company.location || '-'}</td>
                              <td className="px-4 py-3 text-gray-700">
                                {new Date(company.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleVerifyCompany(company.id, true)}
                                  disabled={verifyingCompanyId === company.id}
                                  className="px-3 py-1 rounded-lg text-xs font-medium text-white transition-all disabled:opacity-50"
                                  style={{ backgroundColor: COLORS.admin.primary }}
                                >
                                  {verifyingCompanyId === company.id ? 'Verifying...' : 'Verify'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No companies pending verification
                    </div>
                  )}
                </div>
              )}

              {/* Audit Logs Tab */}
              {activeTab === 'audit' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                      Audit Logs
                    </h3>
                    <button
                      onClick={loadAuditLogs}
                      disabled={isLoadingAudit}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 disabled:opacity-50"
                    >
                      Refresh
                    </button>
                  </div>

                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      placeholder="Filter by Company ID"
                      value={auditCompanyFilter}
                      onChange={(e) => setAuditCompanyFilter(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Filter by Action"
                      value={auditActionFilter}
                      onChange={(e) => setAuditActionFilter(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={loadAuditLogs}
                      className="px-6 py-2 rounded-lg font-medium text-white transition-all"
                      style={{ backgroundColor: COLORS.admin.primary }}
                    >
                      Apply Filters
                    </button>
                  </div>

                  {isLoadingAudit ? (
                    <div className="text-center py-12">
                      <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                        style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                      />
                    </div>
                  ) : auditLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Timestamp</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Company ID</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Field</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Old → New</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Performed By</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {auditLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-gray-700 text-xs">
                                {new Date(log.created_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-gray-700">{log.company_id}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {log.action}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-700">{log.field_name || '-'}</td>
                              <td className="px-4 py-3 text-gray-700 text-xs">
                                {log.old_value || log.new_value ? (
                                  <>
                                    <span className="text-red-600">{log.old_value || '(empty)'}</span>
                                    {' → '}
                                    <span className="text-green-600">{log.new_value || '(empty)'}</span>
                                  </>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700">{log.performed_by}</td>
                              <td className="px-4 py-3 text-gray-700">{log.source}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No audit logs found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

